import { useEffect, useState } from 'react';

import { Bitcoin, Building2, CreditCard, Smartphone } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import { getAvailableWithdrawInstruments } from '../../service/getWithdrawInstruments';
import {
  AvailableTransactionInstrumentDTO,
  PaymentMethodType,
  TransactionMethodInstrument,
} from '../../service/models';

export interface WithdrawMethodOption {
  key: PaymentMethodType;
  icon: LucideIcon;
  title: string;
  /** All instruments backing this option (e.g. both SEPA and SWIFT for bank). */
  instruments: AvailableTransactionInstrumentDTO[];
}

/** Instruments that should be merged into the single "Bank Transfer" card. */
const BANK_TRANSFER_INSTRUMENTS: Set<TransactionMethodInstrument> = new Set<TransactionMethodInstrument>([
  'BANK_TRANSFER_SEPA',
  'BANK_TRANSFER_SWIFT',
]);

const INSTRUMENT_TO_PAYMENT_METHOD: Partial<Record<TransactionMethodInstrument, PaymentMethodType>> = {
  CREDIT_CARD: 'CARD',
  CRYPTO: 'CRYPTO',
  TRADING_ACCOUNT: 'TRADING_ACCOUNT',
};

const INSTRUMENT_ICON: Partial<Record<TransactionMethodInstrument, LucideIcon>> = {
  CREDIT_CARD: CreditCard,
  CRYPTO: Bitcoin,
  TRADING_ACCOUNT: Smartphone,
};

const INSTRUMENT_TITLE: Partial<Record<TransactionMethodInstrument, string>> = {
  CREDIT_CARD: 'Cards & Cash',
  CRYPTO: 'Cryptocurrency',
  TRADING_ACCOUNT: 'RFX Trading Account',
};

interface UseAvailableWithdrawInstrumentsResult {
  methods: WithdrawMethodOption[];
  loading: boolean;
  error: string | null;
}

export const useAvailableWithdrawInstruments = (): UseAvailableWithdrawInstrumentsResult => {
  const [methods, setMethods] = useState<WithdrawMethodOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getAvailableWithdrawInstruments()
      .then((instruments) => {
        const options: WithdrawMethodOption[] = [];

        // Collect bank transfer instruments (SEPA / SWIFT) into one option
        const bankInstruments = instruments.filter((i) => BANK_TRANSFER_INSTRUMENTS.has(i.instrument));
        if (bankInstruments.length > 0) {
          options.push({
            key: 'BANK',
            icon: Building2,
            title: 'Bank Transfer',
            instruments: bankInstruments,
          });
        }

        // Map remaining (non-bank) instruments 1:1
        for (const item of instruments) {
          if (BANK_TRANSFER_INSTRUMENTS.has(item.instrument)) continue;

          const paymentMethod = INSTRUMENT_TO_PAYMENT_METHOD[item.instrument];
          if (paymentMethod == null) continue;

          options.push({
            key: paymentMethod,
            icon: INSTRUMENT_ICON[item.instrument] ?? CreditCard,
            title: INSTRUMENT_TITLE[item.instrument] ?? item.instrument,
            instruments: [item],
          });
        }

        setMethods(options);
      })
      .catch((err) => {
        setError(typeof err === 'string' ? err : 'UNKNOWN_ERROR');
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  return { methods, loading, error };
};
